import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BrandLogo from '@/components/BrandLogo';
import { ArrowRight, Copy, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {}
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setDoc(null);
      return;
    }

    (async () => {
      try {
        const resp = await fetch(`${API}/share/${token}`);
        if (resp.ok) {
          setDoc(await resp.json());
        }
      } catch {}
      finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <Loader2 className="animate-spin" size={24} style={{ color: 'var(--accent-primary)' }} />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="text-center">
          <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>Document not found</p>
          <button onClick={() => navigate('/')} className="advoq-btn-primary text-sm">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg-base)', minHeight: '100vh' }} data-testid="share-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)' }}>
        <div className="flex items-center gap-2">
          <BrandLogo variant="mark" className="h-5 w-5 object-contain" />
          <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-display)' }}>Created with Advoq</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copyShareLink} className="advoq-btn-ghost text-xs py-1.5 px-4 flex items-center gap-1">
            <Copy size={12} /> Copy link
          </button>
          <button data-testid="share-signup-btn" onClick={() => navigate('/auth')} className="advoq-btn-primary text-xs py-1.5 px-4 flex items-center gap-1">
            Sign up free <ArrowRight size={12} />
          </button>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-display)' }}>{doc.title}</h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
              {doc.type?.replace(/_/g, ' ')}
            </span>
            {doc.subtitle && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-glow)', color: 'var(--accent-primary)' }}>
                {doc.subtitle}
              </span>
            )}
            {doc.court && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{doc.court}</span>}
          </div>
        </div>
        {doc.trust_note && (
          <div className="advoq-card p-4 mb-4">
            <p className="text-xs uppercase tracking-[0.18em]" style={{ color: 'var(--text-muted)' }}>Trust note</p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{doc.trust_note}</p>
          </div>
        )}
        <div className="advoq-card p-6">
          <pre className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
            {doc.content}
          </pre>
        </div>
      </div>
    </div>
  );
}
